import React, { useState, useEffect, useRef } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import Editor from '@monaco-editor/react';
import Handlebars from 'handlebars';
import { apiFetch, apiDownload, apiOpenBlob } from '../../lib/api';
import { usePermesso } from '../../context/PermessiContext';
import ApiInspector from '../../components/ApiInspector';
import { API_DOCS } from './TemplateEditorPage.api';
import { MODULI_REPORT, MODULO_REPORT_LABEL } from '../../config/moduliReport';

// ── Tipi ──────────────────────────────────────────────────────────────────
interface SmtpOption { id: number; tipo: string; fromName: string; fromEmail: string }
interface SocietaSorgente { id: number; nome: string; numTemplate: number }
interface TemplateSorgente {
  id: number;
  nome: string;
  moduloGestionale: string;
  attivo: boolean;
  dimensioneHbs: number;
}
interface ReportTemplate {
  id: number;
  societaId: number;
  nome: string;
  descrizione: string | null;
  moduloGestionale: string;
  contenutoHbs: string;
  datiCampione: string | null;
  cssOverride: string | null;
  smtpId: number | null;
  smtp: SmtpOption | null;
  mittenteNomeOverride: string | null;
  mittenteEmailOverride: string | null;
  oggettoEmailDefault: string | null;
  corpoEmailDefault: string | null;
  attivo: boolean;
}

// ── Dati campione default per modulo ──────────────────────────────────────
const DATI_CAMPIONE_DEFAULT: Record<string, string> = {
  // Ridotto da "hbs e json reports/commessa/modello-dati.json" (righe tagliate a 2)
  COMMESSA: JSON.stringify({"commessa": {"id": 28, "numero": "BGRES-2026/028", "descrizione": "Residenza Le Querce — pavimenti e rivestimenti", "stato": "IN_CORSO", "dataApertura": "2026-03-02T08:00:00.000Z", "dataChiusura": null, "budgetIniziale": "28500", "indirizzoLavori": "Via dei Mille 12, Bergamo", "clienteRagioneSociale": "Costruzioni Alfa S.p.A.", "clientePartitaIva": "09876543210"}, "societa": {"nome": "Superfici Orobie", "partitaIva": "01234567890", "codiceFiscale": null, "indirizzo": "Via dell'Artigianato 14, 24050 Ghisalba (BG)", "pec": "superficiorobie@pec.it", "ragioneSociale": "Superfici Orobie S.r.l.", "dominio": "superficiorobie.it"}, "sede": {"nome": "Sede Ghisalba", "logoBase64": null}, "responsabile": {"nomeCompleto": "Marco Rinaldi", "email": "m.rinaldi@esempio.it"}, "ordiniClienti": [{"numero": "BGRES-2026/0044", "descrizione": "Pavimenti e rivestimenti appartamenti piano terra e primo", "dataOrdine": "2026-03-10T00:00:00.000Z", "stato": "IN_LAVORAZIONE", "importoTotale": "22552", "righe": [{"descrizione": "Fornitura e posa gres porcellanato 60x60 beige, incollato", "quantita": "420", "unitaMisura": "mq", "prezzoUnitario": "38", "importoTotale": "15960"}, {"descrizione": "Fornitura e posa rivestimento bagni in gres 30x60", "quantita": "96", "unitaMisura": "mq", "prezzoUnitario": "42", "importoTotale": "4032"}]}, {"numero": "BGRES-2026/0051", "descrizione": "Variante parquet zona notte", "dataOrdine": "2026-05-18T00:00:00.000Z", "stato": "CONFERMATO", "importoTotale": "4794", "righe": [{"descrizione": "Fornitura e posa parquet rovere spazzolato 14 mm, posa flottante", "quantita": "58", "unitaMisura": "mq", "prezzoUnitario": "78", "importoTotale": "4524"}, {"descrizione": "Materassino fonoassorbente 2 mm", "quantita": "60", "unitaMisura": "mq", "prezzoUnitario": "4.5", "importoTotale": "270"}]}], "sal": [{"numero": "BGRES-2026/0003", "stato": "FATTURATO", "dataEmissione": "2026-05-29T08:00:00.000Z", "dataApprovazione": "2026-06-03T08:00:00.000Z", "importoTotale": "7380", "righe": [{"descrizione": "Fornitura e posa gres porcellanato 60x60 beige, incollato", "quantitaTotale": "420", "quantitaCorrente": "150", "prezzoUnitario": "38", "importoCorrente": "5700"}, {"descrizione": "Fornitura e posa rivestimento bagni in gres 30x60", "quantitaTotale": "96", "quantitaCorrente": "40", "prezzoUnitario": "42", "importoCorrente": "1680"}]}, {"numero": "BGRES-2026/0007", "stato": "EMESSO", "dataEmissione": "2026-10-20T09:00:00.000Z", "dataApprovazione": null, "importoTotale": "5670", "righe": [{"descrizione": "Fornitura e posa gres porcellanato 60x60 beige, incollato", "quantitaTotale": "420", "quantitaCorrente": "120", "prezzoUnitario": "38", "importoCorrente": "4560"}, {"descrizione": "Battiscopa coordinato", "quantitaTotale": "80", "quantitaCorrente": "80", "prezzoUnitario": "9.5", "importoCorrente": "760"}]}], "distinte": [{"numero": 1, "tipo": "MERCE", "tipoLabel": "Merce", "stato": "CONFERMATA", "righe": [{"descrizione": "Gres porcellanato Stream 60x60 beige, rettificato", "quantita": "440", "unitaMisura": "mq"}, {"descrizione": "Gres porcellanato Stream 30x60 beige", "quantita": "102", "unitaMisura": "mq"}]}, {"numero": 2, "tipo": "LAVORAZIONI", "tipoLabel": "Lavorazioni", "stato": "BOZZA", "righe": [{"descrizione": "Posa pavimento in gres 60x60 incollato", "quantita": "420", "unitaMisura": "mq"}, {"descrizione": "Posa rivestimento bagni in gres 30x60", "quantita": "96", "unitaMisura": "mq"}]}], "riepilogo": {"totaleOrdini": 27346, "totaleSal": 14610, "avanzamento": 53}, "generatoIl": "2026-10-26T08:00:00.000Z"}, null, 2),
  // Ridotto da "hbs e json reports/fattura-cliente/modello-dati.json" (righe tagliate a 2)
  FATTURA: JSON.stringify({"fattura": {"id": 31, "numero": "BGRES-2026/0058", "tipoFattura": "IMMEDIATA", "tipoLabel": "Fattura immediata", "tipoDocumento": "Fattura su SAL", "bozza": false, "stato": "EMESSA", "dataDocumento": "2026-10-22T00:00:00.000Z", "dataEmissione": "2026-10-22T09:15:00.000Z", "note": "Lavori eseguiti presso il cantiere di Via dei Mille 12, Bergamo.\nRif. conferma d'ordine n. BGRES-2026/0044.", "condizionePagamento": "Bonifico 60 gg FM+10", "totaleImponibile": "3434.2", "totaleIva": "372.22", "totale": "3806.42", "annullata": false}, "societa": {"nome": "Superfici Orobie", "partitaIva": "01234567890", "codiceFiscale": null, "indirizzo": "Via dell'Artigianato 14, 24050 Ghisalba (BG)", "pec": "superficiorobie@pec.it", "ragioneSociale": "Superfici Orobie S.r.l.", "dominio": "superficiorobie.it"}, "sede": {"logoBase64": null, "nome": "Sede Bergamo", "indirizzo": "Via dell'Artigianato 14", "cap": "24040", "citta": "Comun Nuovo", "provincia": "BG"}, "cliente": {"ragioneSociale": "Costruzioni Alfa S.p.A.", "nome": null, "cognome": null, "partitaIva": "09876543210", "codiceFiscale": null, "indirizzo": "Viale Europa 221", "cap": "24100", "citta": "Bergamo", "provincia": "BG", "indirizzoCompleto": "Viale Europa 221, 24100 Bergamo (BG)"}, "commessa": {"id": 28, "numero": "BGRES-2026/028", "descrizione": "Residenza Le Querce — pavimenti e rivestimenti"}, "righe": [{"pos": 1, "ordinePos": 1, "origine": "SAL n. BGRES-2026/0007", "tipoRiga": "SAL", "descrizione": "Fornitura e posa gres porcellanato 60x60 rettificato, beige", "riferimento": "SAL BGRES-2026/0007 del 20/10/2026", "umVendita": "mq", "quantita": "40.5", "prezzoUnitario": "42", "imponibile": "1701", "aliquotaIva": "10", "iva": "170.1"}, {"pos": 2, "ordinePos": 2, "origine": "SAL n. BGRES-2026/0007", "tipoRiga": "SAL", "descrizione": "Battiscopa in gres coordinato h 8 cm", "riferimento": "SAL BGRES-2026/0007 del 20/10/2026", "umVendita": "ml", "quantita": "30", "prezzoUnitario": "9.5", "imponibile": "285", "aliquotaIva": "10", "iva": "28.5"}], "riepilogoIva": [{"aliquota": 10, "imponibile": 3194.2, "iva": 319.42}, {"aliquota": 22, "imponibile": 240, "iva": 52.8}], "generatoIl": "2026-10-26T08:00:00.000Z"}, null, 2),
  PREVENTIVO: JSON.stringify({ preventivo: { numero: 'PR-001', importoTotale: 45000 }, cliente: { ragioneSociale: 'Acme S.r.l.' }, righe: [] }, null, 2),
  DATI_SOCIETARI: JSON.stringify({
    societa: {
      ragioneSociale: 'Gruppo Bonacina S.r.l.',
      partitaIva: '12345678901',
      codiceFiscale: '12345678901',
      sedeLegale: 'Via Roma 1, 20121 Milano (MI)',
      pec: 'gruppobonacina@pec.it',
    },
    amministratori: [
      { nomeCompleto: 'Mario Rossi', carica: 'Amministratore Unico', dataNomina: '2024-01-15T00:00:00.000Z' },
    ],
    compagineSociale: [
      { socio: 'Mario Rossi', quotaPercentuale: 60 },
      { socio: 'Luca Bianchi', quotaPercentuale: 40 },
    ],
  }, null, 2),
  // Valori VERI dell'ordine GO 40702 (documento SM 2308 del 09/09/2026), letti
  // dalla vista vw_ordini_vendita_stampa: l'anteprima si confronta riga per riga
  // con la stampa originale di GO. Tutto arriva da Gestionale Open tranne
  // notePreparazione, che in GO sta nel modello di stampa e non in una tabella.
  // Payload VERO del caricamento 4 (lotto OBI da Drive, ordini GO 40701 e
  // 40702), righe lette dalla Cloud Function vista-ordini-go. entitaId della
  // stampa e' il CARICAMENTO: il documento e' cumulativo, una pagina per
  // ordine. Tutto arriva da GO tranne notePreparazione, che li' sta nel
  // modello di stampa e non in una tabella.
  ORDINE_GO: JSON.stringify({
      "meta": {
        "generatoIl": "2026-09-10T05:00:45.655Z",
        "generatoDa": null
      },
      "societa": {
        "id": 2,
        "nome": "Gruppo Bonacina srl",
        "ragioneSociale": "GRUPPO BONACINA SRL"
      },
      "caricamento": {
        "id": 4,
        "etichetta": "Drive 2026-09-09 12:05 OBI",
        "insegna": "OBI",
        "origine": "DRIVE",
        "creatoIl": "2026-09-09T10:05:08.383Z",
        "ordiniStampati": 2,
        "ordiniNonInviati": [],
        "progressiviMancantiInGo": []
      },
      "ordini": [
        {
          "progressivo": 40701,
          "codiceDocumento": "C1S",
          "descrizioneDocumento": "CONFERMA D'ORDINE SMARTIX",
          "tipoDocumento": "ordine",
          "serieDocumento": "SM",
          "numeroDocumento": "2307",
          "dataDocumento": "2026-09-09",
          "esercizio": "2026",
          "situazione": "inserito",
          "codiceMagazzino": "SMA",
          "centroCosto": "SM0050",
          "valuta": "EURO",
          "cliente": {
            "codice": "00002038",
            "ragioneSociale": "OBI ITALIA SRL",
            "partitaIva": "00508260973",
            "codiceFiscale": "00508260973"
          },
          "destinazione": {
            "codice": "00000015",
            "nome": "OBI ITALIA SRL SAN GIOVANNI TEATINO 042",
            "nome2": null,
            "via": "V. PO, SNC",
            "cap": "66020",
            "citta": "S. G. TEATINO",
            "provincia": "CH",
            "nazione": "ITALIA"
          },
          "riferimentoCliente": "10000310952",
          "dataRiferimento": "2026-09-09",
          "nostroRiferimento": null,
          "dataConsegna": "2026-09-21",
          "dataConferma": "2026-09-09",
          "vettore": "SISTEMA ITALIA SRL",
          "codiceVettore": "SIA",
          "porto": "PORTO FRANCO",
          "codicePorto": "2",
          "pagamento": "BONIFICO BANCARIO 120 GG FM +10",
          "noteDocumento": "Riportare il ns. nr. ordine su tutta la documentazione e attenersi alle condizioni dacquisto concordate.",
          "notePreparazione": "LA MERCE VA PREPARATA ALMENO 5 GIORNI LAVORATIVI PRIMA DELLA DATA DI CONSEGNA",
          "imponibile": 315,
          "iva": 69.3,
          "totale": 384.3,
          "righe": [
            {
              "riga": 10,
              "codiceArticolo": "XST-SCOLAC60",
              "descrizione": "KIT SCOLAPIATTI ACCIAIO X PENSILI 60CM",
              "descrizione2": null,
              "codiceArticoloCliente": "525130",
              "descrizioneArticoloCliente": null,
              "ean": "8053017891811",
              "unitaMisura": "NR",
              "descrizioneUnitaMisura": "NUMERO",
              "quantita": 1,
              "quantitaEvasa": 0,
              "prezzo": 22.5,
              "importo": 22.5,
              "note": null,
              "pesoNetto": 1.4,
              "pezziConfezione": 1
            },
            {
              "riga": 20,
              "codiceArticolo": "XST-SCOLAC90",
              "descrizione": "KIT SCOLAPIATTI ACCIAIO X PENSILE 90CM",
              "descrizione2": null,
              "codiceArticoloCliente": "525132",
              "descrizioneArticoloCliente": null,
              "ean": "8053017891835",
              "unitaMisura": "NR",
              "descrizioneUnitaMisura": "NUMERO",
              "quantita": 1,
              "quantitaEvasa": 0,
              "prezzo": 31,
              "importo": 31,
              "note": null,
              "pesoNetto": 2,
              "pezziConfezione": 0
            },
            {
              "riga": 30,
              "codiceArticolo": "XST-SCP80",
              "descrizione": "KIT SCOLAPIATTI PER MOBILI DA 80 CM",
              "descrizione2": null,
              "codiceArticoloCliente": "525135",
              "descrizioneArticoloCliente": null,
              "ean": "8053017890463",
              "unitaMisura": "PZ",
              "descrizioneUnitaMisura": "PEZZI",
              "quantita": 1,
              "quantitaEvasa": 0,
              "prezzo": 27.5,
              "importo": 27.5,
              "note": null,
              "pesoNetto": 2.5,
              "pezziConfezione": 1
            },
            {
              "riga": 40,
              "codiceArticolo": "XST-SCP90",
              "descrizione": "KIT SCOLAPIATTI PER MOBILI DA 90 CM",
              "descrizione2": null,
              "codiceArticoloCliente": "525136",
              "descrizioneArticoloCliente": null,
              "ean": "8053017890470",
              "unitaMisura": "PZ",
              "descrizioneUnitaMisura": "PEZZI",
              "quantita": 5,
              "quantitaEvasa": 0,
              "prezzo": 29,
              "importo": 145,
              "note": null,
              "pesoNetto": 2.8,
              "pezziConfezione": 1
            },
            {
              "riga": 50,
              "codiceArticolo": "XST-SCP90B",
              "descrizione": "KIT SCOLAPIATTI PER MOBILI DA 90 CM",
              "descrizione2": "FINITURA VERNICIATA BIANCA",
              "codiceArticoloCliente": "525140",
              "descrizioneArticoloCliente": null,
              "ean": "8053017892795",
              "unitaMisura": "PZ",
              "descrizioneUnitaMisura": "PEZZI",
              "quantita": 2,
              "quantitaEvasa": 0,
              "prezzo": 30,
              "importo": 60,
              "note": null,
              "pesoNetto": 2.8,
              "pezziConfezione": 1
            },
            {
              "riga": 60,
              "codiceArticolo": "XST-PPS45",
              "descrizione": "PORTAPOSATE PER CASSETTO DA 45CM",
              "descrizione2": null,
              "codiceArticoloCliente": "5388038",
              "descrizioneArticoloCliente": null,
              "ean": "8053017890555",
              "unitaMisura": "PZ",
              "descrizioneUnitaMisura": "PEZZI",
              "quantita": 5,
              "quantitaEvasa": 0,
              "prezzo": 5.8,
              "importo": 29,
              "note": null,
              "pesoNetto": 0.8,
              "pezziConfezione": 25
            }
          ]
        },
        {
          "progressivo": 40702,
          "codiceDocumento": "C1S",
          "descrizioneDocumento": "CONFERMA D'ORDINE SMARTIX",
          "tipoDocumento": "ordine",
          "serieDocumento": "SM",
          "numeroDocumento": "2308",
          "dataDocumento": "2026-09-09",
          "esercizio": "2026",
          "situazione": "inserito",
          "codiceMagazzino": "SMA",
          "centroCosto": "SM0050",
          "valuta": "EURO",
          "cliente": {
            "codice": "00002038",
            "ragioneSociale": "OBI ITALIA SRL",
            "partitaIva": "00508260973",
            "codiceFiscale": "00508260973"
          },
          "destinazione": {
            "codice": "00000018",
            "nome": "OBI ITALIA SRL AREZZO 045",
            "nome2": null,
            "via": "V. AMENDOLA, 20",
            "cap": "52100",
            "citta": "LOC. CACCIARELLE",
            "provincia": "AR",
            "nazione": "ITALIA"
          },
          "riferimentoCliente": "10000307835",
          "dataRiferimento": "2026-09-09",
          "nostroRiferimento": null,
          "dataConsegna": "2026-09-21",
          "dataConferma": "2026-09-09",
          "vettore": "SISTEMA ITALIA SRL",
          "codiceVettore": "SIA",
          "porto": "PORTO FRANCO",
          "codicePorto": "2",
          "pagamento": "BONIFICO BANCARIO 120 GG FM +10",
          "noteDocumento": "Riportare il ns. nr. ordine su tutta la documentazione e attenersi alle condizioni dacquisto concordate.",
          "notePreparazione": "LA MERCE VA PREPARATA ALMENO 5 GIORNI LAVORATIVI PRIMA DELLA DATA DI CONSEGNA",
          "imponibile": 419.68,
          "iva": 92.33,
          "totale": 512.01,
          "righe": [
            {
              "riga": 10,
              "codiceArticolo": "XST-SCOLAC60",
              "descrizione": "KIT SCOLAPIATTI ACCIAIO X PENSILI 60CM",
              "descrizione2": null,
              "codiceArticoloCliente": "525130",
              "descrizioneArticoloCliente": null,
              "ean": "8053017891811",
              "unitaMisura": "NR",
              "descrizioneUnitaMisura": "NUMERO",
              "quantita": 1,
              "quantitaEvasa": 0,
              "prezzo": 22.5,
              "importo": 22.5,
              "note": null,
              "pesoNetto": 1.4,
              "pezziConfezione": 1
            },
            {
              "riga": 20,
              "codiceArticolo": "XST-SCOLAC90",
              "descrizione": "KIT SCOLAPIATTI ACCIAIO X PENSILE 90CM",
              "descrizione2": null,
              "codiceArticoloCliente": "525132",
              "descrizioneArticoloCliente": null,
              "ean": "8053017891835",
              "unitaMisura": "NR",
              "descrizioneUnitaMisura": "NUMERO",
              "quantita": 2,
              "quantitaEvasa": 0,
              "prezzo": 31,
              "importo": 62,
              "note": null,
              "pesoNetto": 2,
              "pezziConfezione": 0
            },
            {
              "riga": 30,
              "codiceArticolo": "XST-SCP60",
              "descrizione": "KIT SCOLAPIATTI PER MOBILI DA 60 CM",
              "descrizione2": null,
              "codiceArticoloCliente": "525134",
              "descrizioneArticoloCliente": null,
              "ean": "8053017890456",
              "unitaMisura": "PZ",
              "descrizioneUnitaMisura": "PEZZI",
              "quantita": 3,
              "quantitaEvasa": 0,
              "prezzo": 23.5,
              "importo": 70.5,
              "note": null,
              "pesoNetto": 2,
              "pezziConfezione": 1
            },
            {
              "riga": 40,
              "codiceArticolo": "XST-SCP80",
              "descrizione": "KIT SCOLAPIATTI PER MOBILI DA 80 CM",
              "descrizione2": null,
              "codiceArticoloCliente": "525135",
              "descrizioneArticoloCliente": null,
              "ean": "8053017890463",
              "unitaMisura": "PZ",
              "descrizioneUnitaMisura": "PEZZI",
              "quantita": 1,
              "quantitaEvasa": 0,
              "prezzo": 27.5,
              "importo": 27.5,
              "note": null,
              "pesoNetto": 2.5,
              "pezziConfezione": 1
            },
            {
              "riga": 50,
              "codiceArticolo": "XST-SCP90",
              "descrizione": "KIT SCOLAPIATTI PER MOBILI DA 90 CM",
              "descrizione2": null,
              "codiceArticoloCliente": "525136",
              "descrizioneArticoloCliente": null,
              "ean": "8053017890470",
              "unitaMisura": "PZ",
              "descrizioneUnitaMisura": "PEZZI",
              "quantita": 5,
              "quantitaEvasa": 0,
              "prezzo": 29,
              "importo": 145,
              "note": null,
              "pesoNetto": 2.8,
              "pezziConfezione": 1
            },
            {
              "riga": 60,
              "codiceArticolo": "XST-PPS45",
              "descrizione": "PORTAPOSATE PER CASSETTO DA 45CM",
              "descrizione2": null,
              "codiceArticoloCliente": "5388038",
              "descrizioneArticoloCliente": null,
              "ean": "8053017890555",
              "unitaMisura": "PZ",
              "descrizioneUnitaMisura": "PEZZI",
              "quantita": 5,
              "quantitaEvasa": 0,
              "prezzo": 5.8,
              "importo": 29,
              "note": null,
              "pesoNetto": 0.8,
              "pezziConfezione": 25
            },
            {
              "riga": 70,
              "codiceArticolo": "XST-PPS60",
              "descrizione": "PORTAPOSATE PER CASSETTO DA 60CM",
              "descrizione2": null,
              "codiceArticoloCliente": "5388046",
              "descrizioneArticoloCliente": null,
              "ean": "8053017890579",
              "unitaMisura": "PZ",
              "descrizioneUnitaMisura": "PEZZI",
              "quantita": 5,
              "quantitaEvasa": 0,
              "prezzo": 7.5,
              "importo": 37.5,
              "note": null,
              "pesoNetto": 0.9,
              "pezziConfezione": 25
            },
            {
              "riga": 80,
              "codiceArticolo": "XST-TALAV2",
              "descrizione": "TAPPETINO LAVELLO 30x32 FIORI TRASP.",
              "descrizione2": null,
              "codiceArticoloCliente": "5388053",
              "descrizioneArticoloCliente": null,
              "ean": "8053017890562",
              "unitaMisura": "PZ",
              "descrizioneUnitaMisura": "PEZZI",
              "quantita": 12,
              "quantitaEvasa": 0,
              "prezzo": 2.14,
              "importo": 25.68,
              "note": null,
              "pesoNetto": 0.2,
              "pezziConfezione": 45
            }
          ]
        }
      ]
    }, null, 2),
  MOVIMENTI_ANALITICA: JSON.stringify({
    meta: { generatoIl: '2026-05-21T10:00:00.000Z', generatoDa: { nome: 'Mario', cognome: 'Rossi', email: 'm.rossi@gb.it' } },
    societa: { nome: 'Gruppo Bonacina', ragioneSociale: 'Gruppo Bonacina S.r.l.', coloreAccento: '#5B4FB5' },
    filtri: { dataDal: '2026-01-01', dataAl: '2026-04-30', natura: null, contoCodice: null, contoDescrizione: null },
    righe: [
      { data: '2026-03-10', contoCodice: '01.01.001', contoDescrizione: 'Servizi IT', natura: 'COSTO', importo: 1200, competenzaDal: null, competenzaAl: null, descrizione: 'Canone software', numeroDocumento: 'FT 12', commessaNumero: null, origine: 'MANUALE' },
      { data: '2026-04-02', contoCodice: '02.01.001', contoDescrizione: 'Ricavi vendite smartix', natura: 'RICAVO', importo: 5000, competenzaDal: '2026-01-01', competenzaAl: '2026-12-31', descrizione: 'Vendita annuale', numeroDocumento: null, commessaNumero: 'C-101', origine: 'MANUALE' },
    ],
    totali: { costi: 1200, ricavi: 5000, saldo: 3800, numMovimenti: 2 },
  }, null, 2),
  BILANCIO_ANALITICA: JSON.stringify({
    meta: { generatoIl: '2026-05-21T10:00:00.000Z', generatoDa: { nome: 'Mario', cognome: 'Rossi', email: 'm.rossi@gb.it' } },
    societa: { nome: 'Gruppo Bonacina', ragioneSociale: 'Gruppo Bonacina S.r.l.', coloreAccento: '#5B4FB5' },
    periodo: { anno: 2026, dataDal: '2026-01-01', dataAl: '2026-04-30', periodDays: 120, yearDays: 365 },
    ricavi: {
      righe: [
        { codice: '02', descrizione: 'Smartix', livello: 1, natura: 'RICAVO', consuntivo: 1644.0, budget: 1666.67, scostamento: -22.67, scostamentoPerc: -1.4 },
        { codice: '02.01', descrizione: 'Ricavi vendite smartix', livello: 2, natura: 'RICAVO', consuntivo: 1644.0, budget: 1666.67, scostamento: -22.67, scostamentoPerc: -1.4 },
        { codice: '02.01.001', descrizione: 'Ricavi pattumiere', livello: 3, natura: 'RICAVO', consuntivo: 1644.0, budget: 1666.67, scostamento: -22.67, scostamentoPerc: -1.4 },
      ],
      totaleConsuntivo: 1644.0, totaleBudget: 1666.67,
    },
    costi: {
      righe: [
        { codice: '01', descrizione: 'Holding - servizi generali', livello: 1, natura: 'COSTO', consuntivo: 394.52, budget: 400.0, scostamento: -5.48, scostamentoPerc: -1.4 },
        { codice: '01.01', descrizione: 'Amministrazione', livello: 2, natura: 'COSTO', consuntivo: 394.52, budget: 400.0, scostamento: -5.48, scostamentoPerc: -1.4 },
        { codice: '01.01.001', descrizione: 'Servizi IT', livello: 3, natura: 'COSTO', consuntivo: 394.52, budget: 400.0, scostamento: -5.48, scostamentoPerc: -1.4 },
      ],
      totaleConsuntivo: 394.52, totaleBudget: 400.0,
    },
    risultato: { consuntivo: 1249.48, budget: 1266.67, scostamento: -17.19 },
  }, null, 2),
  // Stessa forma di backend/src/print-engine/dataProviders/sal.js (anche in
  // "hbs e json reports/sal/modello-dati.json"): una riga per origine —
  // riga d'ordine, voce extra da distinta, riga aggiunta a mano — e uno storno acconto.
  SAL: JSON.stringify({
    societa: { nome: 'Gruppo Bonacina S.r.l.', partitaIva: '01234567890', codiceFiscale: null, indirizzo: 'Via Roma 1, 24050 Ghisalba (BG)', pec: 'gruppobonacina@pec.it' },
    sede: { nome: 'Sede Ghisalba', logoBase64: null },
    sal: { id: 12, numero: 'BGRES-2026/0007', etichetta: 'SAL n. BGRES-2026/0007', bozza: false, stato: 'EMESSO', statoLabel: 'Emesso',
      dataEmissione: '2026-10-20T09:00:00.000Z', dataApprovazione: null, riferimentoApprovazione: null, tipoDocumento: 'Stato avanzamento lavori', ordineNumero: 'BGRES-2026/0044' },
    commessa: { numero: 'BGRES-2026/028', descrizione: 'Residenza Le Querce — pavimenti e rivestimenti', indirizzoLavori: 'Via dei Mille 12, Bergamo' },
    cliente: { ragioneSociale: 'Costruzioni Alfa S.p.A.', partitaIva: '09876543210', codiceFiscale: null, indirizzo: 'Viale Europa 221, 24100 Bergamo (BG)' },
    righe: [
      { pos: 1, descrizione: 'Fornitura e posa gres porcellanato 60x60', origine: 'Ordine BGRES-2026/0044 · riga 1', unitaMisura: 'mq',
        quantitaTotale: 420, quantitaPrecedente: 150, quantitaCorrente: 120, quantitaProgressiva: 270, percentuale: 64, prezzoUnitario: 38, importoCorrente: 4560 },
      { pos: 2, descrizione: 'Battiscopa coordinato', origine: 'Voce extra', unitaMisura: 'ml',
        quantitaTotale: 80, quantitaPrecedente: 0, quantitaCorrente: 80, quantitaProgressiva: 80, percentuale: 100, prezzoUnitario: 9.5, importoCorrente: 760 },
      { pos: 3, descrizione: 'Integrazione massetto bagno piano terra', origine: 'Integrazione', unitaMisura: null,
        quantitaTotale: 1, quantitaPrecedente: 0, quantitaCorrente: 1, quantitaProgressiva: 1, percentuale: 100, prezzoUnitario: 350, importoCorrente: 350 },
    ],
    storni: [{ proforma: 'BGRES-2026/0002', dataPagamento: '2026-05-19T00:00:00.000Z', importo: 1000 }],
    totali: { righe: 5670, storni: 1000, netto: 4670, haStorni: true },
    generatoIl: '2026-10-26T08:00:00.000Z',
  }, null, 2),
  // Ridotto da "hbs e json reports/ordine-cliente/modello-dati.json" (righe tagliate a 2)
  ORDINE_CLIENTE: JSON.stringify({"societa": {"nome": "Superfici Orobie S.r.l.", "partitaIva": "01234567890", "codiceFiscale": null, "indirizzo": "Via dell'Artigianato 14, 24050 Ghisalba (BG)", "pec": "superficiorobie@pec.it"}, "sede": {"nome": "Sede Bergamo", "logoBase64": null}, "ordine": {"id": 44, "numero": "BGRES-2026/0044", "etichetta": "Ordine n. BGRES-2026/0044", "bozza": false, "stato": "CONFERMATO", "statoLabel": "Confermato", "descrizione": "Da preventivo BGRES-2026/0097", "dataOrdine": "2026-09-14T00:00:00.000Z", "dataConferma": "2026-09-16T08:30:00.000Z", "note": "Consegna dei materiali in cantiere dal lunedì al venerdì, 8:00–17:00, con preavviso di 48 ore.\nIl referente di cantiere sarà comunicato all'avvio dei lavori.", "modalitaEvasione": "SAL", "modalitaEvasioneLabel": "A SAL", "flusso": "VENDITA", "soloImporto": false, "condizionePagamento": "Bonifico 60 gg FM+10", "preventivoNumero": "BGRES-2026/0097", "ivaDefault": {"codice": "10", "descrizione": "IVA 10% agevolata — edilizia abitativa", "aliquota": 10}}, "commessa": {"numero": "BGRES-2026/028", "descrizione": "Residenza Le Querce — pavimenti e rivestimenti", "indirizzoLavori": "Via dei Mille 12, 24125 Bergamo (BG)"}, "cliente": {"ragioneSociale": "Costruzioni Alfa S.p.A.", "partitaIva": "09876543210", "codiceFiscale": null, "indirizzo": "Viale Europa 221, 24100 Bergamo (BG)", "email": "acquisti@costruzionialfa.it", "telefono": "035 412 7788"}, "acquirente": {"nome": "Mario Rossi"}, "sezioni": [{"titolo": null, "percorso": null, "livello": 0, "descrizione": null, "righe": [{"pos": 1, "tipoRiga": "DESCRITTIVA", "descrittiva": true, "aCorpo": false, "ricorrente": false, "codice": null, "descrizione": "Le quantità sono stimate da progetto: la contabilizzazione avverrà a misura nei SAL.", "descrizioneEstesa": null, "unitaMisura": null, "quantita": null, "prezzoUnitario": null, "sconto": null, "importo": null, "aliquotaIva": 10, "alternativa": null, "ricorrenza": null}, {"pos": 2, "tipoRiga": "A_CORPO", "descrittiva": false, "aCorpo": true, "ricorrente": false, "codice": "ONERI-CANT", "descrizione": "Allestimento cantiere, trasporto e scarico materiali al piano", "descrizioneEstesa": null, "unitaMisura": "corpo", "quantita": 1, "prezzoUnitario": 850, "sconto": null, "importo": 850, "aliquotaIva": 10, "alternativa": null, "ricorrenza": null}], "totale": 850}, {"titolo": "Piano terra", "percorso": "Piano terra", "livello": 1, "descrizione": "Zona giorno, ingresso e disimpegni", "righe": [{"pos": 3, "tipoRiga": "ARTICOLO", "descrittiva": false, "aCorpo": false, "ricorrente": false, "codice": "GRES-6060-BE", "descrizione": "Gres porcellanato 60x60 rettificato, beige", "descrizioneEstesa": "Fornitura e posa a colla su massetto esistente.\nFughe 2 mm, sigillante cementizio grigio.", "unitaMisura": "mq", "quantita": 86.5, "prezzoUnitario": 42, "sconto": null, "importo": 3633, "aliquotaIva": 10, "alternativa": null, "ricorrenza": null}, {"pos": 4, "tipoRiga": "ARTICOLO", "descrittiva": false, "aCorpo": false, "ricorrente": false, "codice": "BATT-GRES-8", "descrizione": "Battiscopa in gres coordinato h 8 cm", "descrizioneEstesa": null, "unitaMisura": "ml", "quantita": 62, "prezzoUnitario": 9.5, "sconto": 5, "importo": 559.55, "aliquotaIva": 10, "alternativa": null, "ricorrenza": null}], "totale": 4192.55}], "haSezioni": true, "sconto": {"importo": -497.99, "descrizione": "Sconto commerciale", "imponibileLordo": 11997.99}, "acconto": {"percentuale": 30, "imponibile": 3450, "iva": 345, "totale": 3795, "condizionePagamento": "Bonifico alla conferma d'ordine"}, "totali": {"imponibile": 11500, "iva": 1150, "totale": 12650}, "generatoIl": "2026-10-26T08:00:00.000Z"}, null, 2),
  // Ridotto da "hbs e json reports/distinta/modello-dati.json" (righe tagliate a 2)
  DISTINTA: JSON.stringify({"societa": {"nome": "Superfici Orobie S.r.l.", "partitaIva": "01234567890", "codiceFiscale": null, "indirizzo": "Via dell'Artigianato 14, 24050 Ghisalba (BG)", "pec": "superficiorobie@pec.it"}, "sede": {"nome": "Sede Ghisalba", "logoBase64": null}, "distinta": {"id": 9, "numero": "BGRES-2026/0044-D3", "etichetta": "Distinta n. BGRES-2026/0044-D3", "tipo": "MERCE", "tipoLabel": "Distinta merce", "titolo": "DISTINTA MERCE", "merce": true, "lavorazioni": false, "addebito": "CLIENTE", "addebitoLabel": "Addebito al cliente", "addebitoCliente": true, "descrizione": "Materiale per i bagni del piano primo, variante richiesta dalla direzione lavori", "stato": "CONFERMATA", "bozza": false, "dataCreazione": "2026-10-20T08:15:00.000Z", "confermataIl": "2026-10-21T14:05:00.000Z", "confermataDa": "Luca Ferrari", "creataDa": "Giulia Colombo", "ordineNumero": "BGRES-2026/0044"}, "commessa": {"numero": "BGRES-2026/028", "descrizione": "Residenza Le Querce — pavimenti e rivestimenti", "indirizzoLavori": "Via dei Mille 12, 24122 Bergamo (BG)"}, "cliente": {"ragioneSociale": "Costruzioni Alfa S.p.A.", "indirizzo": "Viale Europa 221, 24100 Bergamo (BG)"}, "righe": [{"pos": 1, "codice": "GRS-3060-BG", "descrizione": "Gres porcellanato 30x60 beige per rivestimento bagni", "unitaMisura": "mq", "quantita": 36.4, "note": "Lotto 24B, stesso tono della prima consegna", "origine": "Ordine BGRES-2026/0044 · riga 3"}, {"pos": 2, "codice": "GRS-6060-BG", "descrizione": "Gres porcellanato 60x60 rettificato beige, sp. 9 mm", "unitaMisura": "mq", "quantita": 18.72, "note": null, "origine": "Ordine BGRES-2026/0044 · riga 2"}], "generatoIl": "2026-10-26T08:00:00.000Z"}, null, 2),
  // Ridotto da "hbs e json reports/ddt/modello-dati.json" (righe tagliate a 2)
  DDT: JSON.stringify({"ddt": {"id": 18, "numero": "BGRES-2026/0031", "tipo": "VENDITA", "tipoLabel": "Vendita", "bozza": false, "annullato": false, "stato": "EMESSO", "dataEmissione": "2026-10-22T07:30:00.000Z", "dataTrasporto": "2026-10-22T07:30:00.000Z", "causaleTrasporto": "Vendita", "aspettoBeni": "Bancali filmati e scatole", "colli": 6, "pesoKg": "1874.5", "porto": "Franco destino", "vettore": "Autotrasporti Valle del Serio S.n.c., Via Provinciale 8, 24021 Albino (BG)", "annotazioni": "Scarico con sponda idraulica al piano terra, accesso carrabile da Via dei Mille.\nPreavvisare il capocantiere 30 minuti prima dell'arrivo."}, "societa": {"nome": "Superfici Orobie", "partitaIva": "01234567890", "codiceFiscale": null, "indirizzo": "Via dell'Artigianato 14, 24050 Ghisalba (BG)", "pec": "superficiorobie@pec.it", "ragioneSociale": "Superfici Orobie S.r.l.", "dominio": "superficiorobie.it"}, "sede": {"logoBase64": null, "nome": "Sede Ghisalba", "indirizzo": "Via dell'Artigianato 14", "cap": "24050", "citta": "Ghisalba", "provincia": "BG"}, "cliente": {"ragioneSociale": "Costruzioni Alfa S.p.A.", "nome": null, "cognome": null, "partitaIva": "09876543210", "codiceFiscale": null, "indirizzo": "Viale Europa 221", "cap": "24100", "citta": "Bergamo", "provincia": "BG", "indirizzoCompleto": "Viale Europa 221, 24100 Bergamo (BG)"}, "destinazione": {"descrizione": "Cantiere Residenza Le Querce", "indirizzo": "Via dei Mille 12", "cap": "24122", "citta": "Bergamo", "provincia": "BG", "indirizzoCompleto": "Via dei Mille 12, 24122 Bergamo (BG)"}, "ordine": {"numero": "BGRES-2026/0044"}, "commessa": {"numero": "BGRES-2026/028", "descrizione": "Residenza Le Querce — pavimenti e rivestimenti"}, "generatoIl": "2026-10-26T08:00:00.000Z", "movimentoMagazzino": {"numero": 58, "anno": 2026}, "righe": [{"ordinePos": 1, "codice": "GRS-6060-BG", "descrizione": "Gres porcellanato 60x60 rettificato beige, sp. 9 mm", "umVendita": "mq", "quantita": "142.56", "note": "Lotto 24B — tono 3: tenere separato dagli altri lotti"}, {"ordinePos": 2, "codice": "GRS-3060-BG", "descrizione": "Gres porcellanato 30x60 beige per rivestimento bagni", "umVendita": "mq", "quantita": "48.6", "note": null}]}, null, 2),
  // Ridotto da "hbs e json reports/proforma-acconto/modello-dati.json" (righe tagliate a 2)
  PROFORMA_ACCONTO: JSON.stringify({"societa": {"nome": "Superfici Orobie S.r.l.", "partitaIva": "01234567890", "codiceFiscale": null, "indirizzo": "Via dell'Artigianato 14, 24050 Ghisalba (BG)", "pec": "superficiorobie@pec.it"}, "sede": {"nome": "Sede Bergamo", "logoBase64": null}, "proforma": {"id": 12, "numero": "BGRES-2026/0012", "etichetta": "Proforma n. BGRES-2026/0012", "stato": "DA_INCASSARE", "statoLabel": "Da incassare", "pagata": false, "annullata": false, "origine": "DA_CONVERSIONE_ORDINE", "descrizione": "Acconto sui lavori di posa pavimenti e rivestimenti", "note": "Acconto previsto dalla conferma d'ordine n. BGRES-2026/0044.\nL'importo sarà stornato in quota nei SAL successivi.", "dataEmissione": "2026-09-16T09:00:00.000Z", "dataPagamento": null, "dataAnnullamento": null, "motivoAnnullamento": null, "condizionePagamento": "Bonifico alla conferma d'ordine", "condizioneStampa": "Il pagamento va effettuato con bonifico bancario indicando in causale il numero della presente proforma.\nL'avvio dei lavori in cantiere è subordinato all'accredito dell'acconto.", "ordineNumero": "BGRES-2026/0044", "percentualeOrdine": 30}, "commessa": {"numero": "BGRES-2026/028", "descrizione": "Residenza Le Querce — pavimenti e rivestimenti", "indirizzoLavori": "Via dei Mille 12, 24125 Bergamo (BG)"}, "cliente": {"ragioneSociale": "Costruzioni Alfa S.p.A.", "partitaIva": "09876543210", "codiceFiscale": null, "indirizzo": "Viale Europa 221, 24100 Bergamo (BG)"}, "acquirente": null, "iva": {"codice": "10", "descrizione": "IVA 10% agevolata — edilizia abitativa", "aliquota": 10, "splitPayment": false, "reverseCharge": false}, "totali": {"imponibile": 3450, "iva": 345, "totale": 3795, "daVersare": 3795}, "generatoIl": "2026-10-26T08:00:00.000Z"}, null, 2),
  // Ridotto da "hbs e json reports/ordine-fornitore/modello-dati.json" (righe tagliate a 2)
  ORDINE_FORNITORE: JSON.stringify({"societa": {"nome": "Superfici Orobie S.r.l.", "partitaIva": "01234567890", "codiceFiscale": null, "indirizzo": "Via dell'Artigianato 14, 24050 Ghisalba (BG)", "pec": "superficiorobie@pec.it"}, "sede": {"nome": "Sede Ghisalba", "logoBase64": null}, "ordine": {"id": 37, "numero": "BGOF-2026/0058", "etichetta": "Ordine n. BGOF-2026/0058", "bozza": false, "annullato": false, "stato": "EMESSO", "statoLabel": "Emesso", "dataEmissione": "2026-10-21T08:30:00.000Z", "note": "Consegna al magazzino con preavviso telefonico di almeno 24 ore.\nScarico con sponda idraulica; ricevimento merci dal lunedì al venerdì, 8:00-12:00 e 13:30-17:00.", "commesse": "BGRES-2026/028, BGRES-2026/031"}, "fornitore": {"ragioneSociale": "Ceramiche Valle Serio S.r.l.", "partitaIva": "04455660128", "codiceFiscale": null, "indirizzo": "Via dell'Artigianato 18, 24020 Gorle (BG)", "pec": "ordini@pec.ceramichevalleserio.it", "telefono": "035 4561230"}, "consegna": {"presso": "Sede Ghisalba", "indirizzo": "Via Roma 1, 24050 Ghisalba (BG)"}, "righe": [{"pos": 1, "codice": "GRS-6060-BE", "descrizione": "Gres porcellanato Stream 60x60 beige, rettificato, sp. 8,5 mm", "unitaMisura": "mq", "quantita": 420, "prezzoUnitario": 21.8, "importo": 9156, "note": null, "origine": "Commessa BGRES-2026/028 · RDA 14/2026"}, {"pos": 2, "codice": "GRS-3060-BE", "descrizione": "Gres porcellanato Stream 30x60 beige, rivestimento bagni", "unitaMisura": "mq", "quantita": 96, "prezzoUnitario": 23.5, "importo": 2256, "note": "Stesso lotto di produzione della riga 1", "origine": "Commessa BGRES-2026/028 · RDA 14/2026"}], "totali": {"imponibile": 13411.6, "righeSenzaPrezzo": 2, "haPrezzi": true}, "generatoIl": "2026-10-26T08:00:00.000Z"}, null, 2),
  // Ridotto da "hbs e json reports/carico-merce/modello-dati.json" (righe tagliate a 2)
  CARICO_MERCE: JSON.stringify({"societa": {"nome": "Superfici Orobie S.r.l.", "partitaIva": "01234567890", "codiceFiscale": null, "indirizzo": "Via dell'Artigianato 14, 24050 Ghisalba (BG)", "pec": "superficiorobie@pec.it"}, "sede": {"nome": "Sede Ghisalba", "logoBase64": null}, "carico": {"id": 214, "numero": "37/2026", "etichetta": "Carico n. 37/2026", "bozza": false, "stornato": false, "stato": "CONFERMATO", "statoLabel": "Confermato", "data": "2026-10-22T00:00:00.000Z", "descrizione": "Ricevimento gres e collanti per la commessa Residenza Le Querce", "note": "Due scatole di gres 60x60 con spigoli scheggiati: segnalate al vettore con riserva scritta sul DDT.\nResto della merce conforme a quantità e lotto.", "causale": "CARFOR — Carico da acquisto merce fornitore", "ddtFornitoreNumero": "4417/B", "ddtFornitoreData": "2026-10-21T00:00:00.000Z"}, "fornitore": {"ragioneSociale": "Ceramiche del Secchia S.p.A.", "partitaIva": "02468013579", "codiceFiscale": null, "indirizzo": "Via Radici in Piano 300, 41049 Sassuolo (MO)"}, "deposito": {"nome": "Magazzino centrale", "indirizzo": "Via dell'Artigianato 14, 24050 Ghisalba (BG)"}, "commessa": {"numero": "BGRES-2026/028", "descrizione": "Residenza Le Querce — pavimenti e rivestimenti"}, "righe": [{"pos": 1, "codice": "GRS-6060-BG", "descrizione": "Gres porcellanato 60x60 rettificato beige, sp. 9 mm", "unitaMisura": "mq", "quantita": 142.56, "prezzoUnitario": 18.4, "valore": 2623.1, "note": "Lotto 24B — tono 3"}, {"pos": 2, "codice": "GRS-3060-BG", "descrizione": "Gres porcellanato 30x60 beige per rivestimento", "unitaMisura": "mq", "quantita": 85, "prezzoUnitario": 16.9, "valore": 1436.5, "note": null}], "totali": {"quantitaRighe": 8, "valore": 5360.8}, "generatoIl": "2026-10-26T08:00:00.000Z"}, null, 2),
  GENERICO: JSON.stringify({ titolo: 'Documento', data: '2025-04-01T00:00:00.000Z', contenuto: '...' }, null, 2),
};

function getDatiCampioneDefault(modulo: string): string {
  return DATI_CAMPIONE_DEFAULT[modulo] || DATI_CAMPIONE_DEFAULT.GENERICO;
}

// ── Handlebars helpers (stessi del backend) ──────────────────────────────
Handlebars.registerHelper('formatDate', (d: unknown) => {
  if (!d) return '\u2014';
  return new Date(String(d)).toLocaleDateString('it-IT');
});
// Formattazione numerica italiana DETERMINISTICA (separatore migliaia '.', decimali ',')
const fmtItaliano = (n: number, minDec: number, maxDec: number): string => {
  const neg = n < 0;
  const parts = Math.abs(n).toFixed(maxDec).split('.');
  let intPart = parts[0];
  let decPart = parts[1] || '';
  while (decPart.length > minDec && decPart.endsWith('0')) decPart = decPart.slice(0, -1);
  intPart = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return (neg ? '-' : '') + intPart + (decPart ? ',' + decPart : '');
};
Handlebars.registerHelper('formatCurrency', (v: unknown) => {
  if (v == null) return '\u2014';
  const n = Number(v);
  return Number.isFinite(n) ? fmtItaliano(n, 2, 2) : '\u2014';
});
Handlebars.registerHelper('formatNumber', (v: unknown) => {
  if (v == null) return '\u2014';
  const n = Number(v);
  return Number.isFinite(n) ? fmtItaliano(n, 0, 3) : '\u2014';
});
Handlebars.registerHelper('upper', (s: unknown) => String(s || '').toUpperCase());
// {{@index}} parte da 0: per scrivere "ordine 1 di 8" serve il successivo.
// ⚠️ Ogni helper va registrato QUI e in print-engine/printEngine.js: l'anteprima
// dell'editor compila nel browser, il PDF sul server. Se manca da una parte
// sola, l'anteprima muore con "Missing helper" mentre il PDF esce bene.
Handlebars.registerHelper('indiceUmano', (i: unknown) => Number(i) + 1);
Handlebars.registerHelper('gt', (a: unknown, b: unknown) => Number(a) > Number(b));
Handlebars.registerHelper('abs', (v: unknown) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.abs(n) : v;
});
Handlebars.registerHelper('nl2br', (str: unknown) => {
  if (!str) return '';
  const escaped = Handlebars.escapeExpression(String(str));
  return new Handlebars.SafeString(escaped.replace(/\n/g, '<br>'));
});

// ── SVG icons inline ─────────────────────────────────────────────────────
const ChevronIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
);
const CodeIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="16 18 22 12 16 6" /><polyline points="8 6 2 12 8 18" /></svg>
);
const DbIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><ellipse cx="12" cy="5" rx="9" ry="3" /><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" /><path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" /></svg>
);
const PanelLeftIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" /><line x1="9" y1="3" x2="9" y2="21" /></svg>
);
const MenuIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" /></svg>
);


export default function TemplateEditorPage() {
  const location = useLocation();
  const isRuntime = location.pathname.startsWith('/print/');
  const { id, templateId } = useParams<{ id?: string; templateId?: string }>();
  const resolvedId = isRuntime ? templateId : id;
  const { can } = usePermesso();

  // Parametri runtime da query string
  const searchParams = new URLSearchParams(location.search);
  const entitaType = searchParams.get('entitaType') || 'commessa';
  const entitaId = searchParams.get('entitaId') || '';
  const autoOpenInvia = searchParams.get('autoOpenInvia') === '1';
  // Vista per preventivi con acquirente ('APPALTATORE' | 'ACQUIRENTE' | null)
  const vistaRaw = searchParams.get('vista');
  const vista: 'APPALTATORE' | 'ACQUIRENTE' | null =
    vistaRaw === 'APPALTATORE' || vistaRaw === 'ACQUIRENTE' ? vistaRaw : null;

  // Opzioni di stampa propagate dal PrintModal — default true se assenti
  const conTotaliRaw   = searchParams.get('conTotali');
  const conAllegatiRaw = searchParams.get('conAllegati');
  const conTotali   = conTotaliRaw   === 'false' ? false : true;
  const conAllegati = conAllegatiRaw === 'false' ? false : true;

  // Opzioni specifiche di modulo (passate via query string da PrintModal)
  const extraOpts = (() => {
    // Moduli contabilità analitica: filtri della pagina (esercizio + periodo + altri)
    if (entitaType === 'movimenti_analitica' || entitaType === 'bilancio_analitica') {
      const out: Record<string, string | boolean | number[]> = {};
      ['anno', 'dataDal', 'dataAl', 'natura', 'contoAnaliticoId'].forEach(k => {
        const v = searchParams.get(k);
        if (v !== null && v !== '') out[k] = v;
      });
      return out;
    }
    if (entitaType !== 'dati_societari') return {} as Record<string, string | boolean | number[]>;
    const out: Record<string, string | boolean | number[]> = {};
    const flagKeys = [
      'includiDatiSocietari','includiAmministratori','includiCompagine',
      'includiSedi','includiBanche','includiFinanziamenti','includiBilanci',
    ] as const;
    flagKeys.forEach(k => {
      const v = searchParams.get(k);
      if (v !== null) out[k] = v !== 'false';
    });
    const ids = searchParams.get('bilanciIds');
    if (ids) {
      out.bilanciIds = ids.split(',').map(s => parseInt(s, 10)).filter(n => Number.isInteger(n) && n > 0);
    }
    return out;
  })();
  const applyExtraOptsToParams = (params: URLSearchParams) => {
    Object.entries(extraOpts).forEach(([k, v]) => {
      if (Array.isArray(v)) {
        if (v.length > 0) params.set(k, v.join(','));
      } else if (v !== undefined && v !== null) {
        params.set(k, String(v));
      }
    });
  };

  // Destinatari suggeriti da query string (fallback per design mode)
  const destinatariRaw = searchParams.get('destinatari');
  const destinatariDaQueryParam: Array<{ label: string; nome: string; email: string }> =
    destinatariRaw ? JSON.parse(decodeURIComponent(destinatariRaw)) : [];

  // ── Stato ──────────────────────────────────────────────────────────────
  const [template, setTemplate] = useState<ReportTemplate | null>(null);
  const [loading, setLoading] = useState(true);

  // Numero allegati PDF da accodare al PDF finale (popolato dal data provider preventivo)
  const [numeroAllegatiPdfStampabili, setNumeroAllegatiPdfStampabili] = useState(0);

  // Metadati runtime (_meta) per config mittente sede e destinatari suggeriti
  const [metaDati, setMetaDati] = useState<{
    mailConfig: {
      smtpId: number | null;
      mittenteEmail: string | null;
      mittenteNome: string | null;
      ccnSelf: boolean;
      ccnAggiuntivi: string[];
      sedeConfigured: boolean;
    } | null;
    destinatariSuggeriti: Array<{ label: string; email: string; nome: string; principale?: boolean }>;
  } | null>(null);

  // Campi sidebar
  const [nome, setNome] = useState('');
  const [descrizione, setDescrizione] = useState('');
  const [moduloGestionale, setModuloGestionale] = useState('GENERICO');
  const [cssOverride, setCssOverride] = useState('');
  const [smtpId, setSmtpId] = useState('');
  const [mittenteNomeOverride, setMittenteNomeOverride] = useState('');
  const [mittenteEmailOverride, setMittenteEmailOverride] = useState('');
  const [oggettoEmailDefault, setOggettoEmailDefault] = useState('');
  const [corpoEmailDefault, setCorpoEmailDefault] = useState('');
  const [smtpOptions, setSmtpOptions] = useState<SmtpOption[]>([]);

  // Editor
  const [activeTab, setActiveTab] = useState<'struttura' | 'dati'>('struttura');
  const [contenutoHbs, setContenutoHbs] = useState('');
  const [datiJson, setDatiJson] = useState('');
  const [cursorInfo, setCursorInfo] = useState('Ln 1, Col 1');
  const hbsImportRef = useRef<HTMLInputElement>(null);
  const jsonImportRef = useRef<HTMLInputElement>(null);

  // Preview
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewError, setPreviewError] = useState('');
  const [previewTime, setPreviewTime] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  // Pannelli
  const SIDEBAR_KEY = 'bop_editor_sidebar';
  const LEFT_KEY = 'bop_editor_left';
  const [sidebarOpen, setSidebarOpen] = useState(() =>
    localStorage.getItem(SIDEBAR_KEY) !== 'false');
  const [leftOpen, setLeftOpen] = useState(() =>
    isRuntime ? false : localStorage.getItem(LEFT_KEY) !== 'false');

  // Invia email panel
  const [inviaOpen, setInviaOpen] = useState(false);
  const [inviaDestinatari, setInviaDestinatari] = useState('');
  const [inviaOggetto, setInviaOggetto] = useState('');
  const [inviaCorpo, setInviaCorpo] = useState('');
  const [inviaSelezionati, setInviaSelezionati] = useState<string[]>([]);
  const [inviando, setInviando] = useState(false);

  // Saving
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveOk, setSaveOk] = useState(false);

  // Import da altra società
  const [impOpen, setImpOpen] = useState(false);
  const [impSorgenti, setImpSorgenti] = useState<SocietaSorgente[]>([]);
  const [impSorgenteId, setImpSorgenteId] = useState('');
  const [impTemplates, setImpTemplates] = useState<TemplateSorgente[]>([]);
  const [impTemplateId, setImpTemplateId] = useState('');
  const [impStruttura, setImpStruttura] = useState(true);
  const [impDati, setImpDati] = useState(false);
  const [impCss, setImpCss] = useState(false);
  const [impLoading, setImpLoading] = useState(false);
  const [impErrore, setImpErrore] = useState('');

  // AI editor assist
  const [aiOpen, setAiOpen] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState('');
  const [aiOk, setAiOk] = useState(false);

  // ── Mount — caricamento template ───────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        // @api GET /api/report-templates/:id -> {"success":true,"data":{...}}
        const r = await apiFetch<{ success: boolean; data: ReportTemplate }>(
          `/api/report-templates/${resolvedId}`
        );
        const t = r.data;
        setTemplate(t);
        setNome(t.nome);
        setDescrizione(t.descrizione || '');
        setModuloGestionale(t.moduloGestionale);
        setContenutoHbs(t.contenutoHbs || '');
        setCssOverride(t.cssOverride || '');
        setSmtpId(t.smtpId ? String(t.smtpId) : '');
        setMittenteNomeOverride(t.mittenteNomeOverride || '');
        setMittenteEmailOverride(t.mittenteEmailOverride || '');
        setOggettoEmailDefault(t.oggettoEmailDefault || '');
        setCorpoEmailDefault(t.corpoEmailDefault || '');

        if (isRuntime && entitaId) {
          // @api GET /api/print/data/:templateId?entitaType=commessa&entitaId=N&vista=APPALTATORE|ACQUIRENTE&conTotali=false&conAllegati=false -> {"success":true,"data":{...,"_meta":{...}}}
          const dataUrlParams = new URLSearchParams({
            entitaType,
            entitaId: String(entitaId),
          });
          if (vista) dataUrlParams.set('vista', vista);
          if (!conTotali)   dataUrlParams.set('conTotali', 'false');
          if (!conAllegati) dataUrlParams.set('conAllegati', 'false');
          applyExtraOptsToParams(dataUrlParams);
          const dr = await apiFetch<{ success: boolean; data: Record<string, unknown> }>(
            `/api/print/data/${resolvedId}?${dataUrlParams.toString()}`
          );
          // Salva _meta per il pannello Invia (destinatari suggeriti, config mittente)
          if (dr.data?._meta) {
            setMetaDati(dr.data._meta as typeof metaDati);
          }
          // Estrai opzioni stampa dal payload (popolato dal data provider preventivo)
          const opts = (dr.data as { options?: { numeroAllegatiPdfStampabili?: number } })?.options;
          if (opts && typeof opts.numeroAllegatiPdfStampabili === 'number') {
            setNumeroAllegatiPdfStampabili(opts.numeroAllegatiPdfStampabili);
          } else {
            setNumeroAllegatiPdfStampabili(0);
          }
          setDatiJson(JSON.stringify(dr.data, null, 2));
          // In runtime interpola oggetto e corpo con i dati reali
          try {
            setInviaOggetto(Handlebars.compile(t.oggettoEmailDefault || t.nome)(dr.data));
            setInviaCorpo(Handlebars.compile(t.corpoEmailDefault || '')(dr.data));
          } catch {
            setInviaOggetto(t.oggettoEmailDefault || t.nome);
            setInviaCorpo(t.corpoEmailDefault || '');
          }
        } else {
          setDatiJson(t.datiCampione || getDatiCampioneDefault(t.moduloGestionale));
          setInviaOggetto(t.oggettoEmailDefault || `${t.nome}`);
          setInviaCorpo(t.corpoEmailDefault || '');
        }
      } catch {
        window.close();
      } finally {
        setLoading(false);
      }
    };
    load();

    // Carica SMTP — solo in design: la combo mittente è disabilitata in runtime
    // (lì l'SMTP arriva da _meta.mailConfig) e chi stampa un documento non ha
    // per forza il permesso sulle configurazioni SMTP: la chiamata tornerebbe
    // 403 e farebbe comparire un "Accesso negato" fuori contesto.
    if (!isRuntime) {
      Promise.all([
        // @api GET /api/smtp?tipo=EMAIL -> {"smtp":[...]}
        apiFetch<{ smtp: SmtpOption[] }>('/api/smtp?tipo=EMAIL'),
        // @api GET /api/smtp?tipo=PEC -> {"smtp":[...]}
        apiFetch<{ smtp: SmtpOption[] }>('/api/smtp?tipo=PEC'),
      ])
        .then(([e, p]) => setSmtpOptions([...(e.smtp || []), ...(p.smtp || [])]))
        .catch(() => setSmtpOptions([]));
    }
  }, [resolvedId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Preview client-side con debounce 600ms ─────────────────────────────
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      try {
        const dati = JSON.parse(datiJson || '{}');
        const templateFn = Handlebars.compile(contenutoHbs || '');
        const html = templateFn({ ...dati, css: `<style>${cssOverride || ''}</style>` });
        setPreviewHtml(html);
        setPreviewError('');
        setPreviewTime(new Date().toLocaleTimeString('it-IT'));
      } catch (e: unknown) {
        setPreviewError(e instanceof Error ? e.message : String(e));
      }
    }, 600);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [contenutoHbs, datiJson, cssOverride]);

  // Auto-apre il pannello Invia se richiesto dal chiamante (es. PrintModal azione email)
  useEffect(() => {
    if (autoOpenInvia && template && !loading) {
      setInviaOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOpenInvia, template, loading]);

  // In runtime i destinatari vengono da _meta; in design dal query param (fallback)
  const destinatariSuggeriti = isRuntime
    ? (metaDati?.destinatariSuggeriti || [])
    : destinatariDaQueryParam;

  // ── Import .hbs ────────────────────────────────────────────────────────
  const handleImportHbs = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => setContenutoHbs((ev.target?.result as string) || '');
    reader.readAsText(file);
    e.target.value = '';
  };

  // ── Import .json ───────────────────────────────────────────────────────
  const handleImportJson = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => setDatiJson((ev.target?.result as string) || '');
    reader.readAsText(file);
    e.target.value = '';
  };

  // ── Import da template di un'altra società ─────────────────────────────
  const apriImportSocieta = async () => {
    setImpOpen(true);
    setImpErrore('');
    setImpSorgenteId('');
    setImpTemplates([]);
    setImpTemplateId('');
    // Precheck coerente con la tab su cui si sta lavorando
    setImpStruttura(activeTab === 'struttura');
    setImpDati(activeTab === 'dati');
    setImpCss(false);
    try {
      // @api GET /api/report-templates/importa/societa -> {"success":true,"data":[{"id":"number","nome":"string","numTemplate":"number"}]}
      const r = await apiFetch<{ success: boolean; data: SocietaSorgente[] }>('/api/report-templates/importa/societa');
      setImpSorgenti((r.data || []).filter(s => s.id !== template?.societaId));
    } catch (e: unknown) {
      setImpErrore(e instanceof Error ? e.message : 'Errore nel caricamento delle società');
    }
  };

  const cambiaImpSorgente = async (sid: string) => {
    setImpSorgenteId(sid);
    setImpTemplateId('');
    setImpTemplates([]);
    setImpErrore('');
    if (!sid) return;
    try {
      // @api GET /api/report-templates/importa/templates?sorgenteId= -> {"success":true,"data":[{"id":"number","nome":"string","moduloGestionale":"string","attivo":"boolean","dimensioneHbs":"number"}]}
      const r = await apiFetch<{ success: boolean; data: TemplateSorgente[] }>(
        `/api/report-templates/importa/templates?sorgenteId=${sid}`
      );
      setImpTemplates(r.data || []);
    } catch (e: unknown) {
      setImpErrore(e instanceof Error ? e.message : 'Errore nel caricamento dei template');
    }
  };

  const eseguiImportSocieta = async () => {
    if (!impTemplateId) return;
    if (!impStruttura && !impDati && !impCss) {
      setImpErrore('Seleziona almeno una parte da importare');
      return;
    }
    setImpLoading(true);
    setImpErrore('');
    try {
      // @api GET /api/report-templates/importa/contenuto/:templateId -> {"success":true,"data":{"contenutoHbs":"string","datiCampione":"string","cssOverride":"string"}}
      const r = await apiFetch<{ success: boolean; data: {
        nome: string; contenutoHbs: string; datiCampione: string | null;
        cssOverride: string | null; societa: { nome: string };
      } }>(`/api/report-templates/importa/contenuto/${impTemplateId}`);

      if (impStruttura) setContenutoHbs(r.data.contenutoHbs || '');
      if (impDati && r.data.datiCampione) setDatiJson(r.data.datiCampione);
      if (impCss) setCssOverride(r.data.cssOverride || '');
      setImpOpen(false);
    } catch (e: unknown) {
      setImpErrore(e instanceof Error ? e.message : 'Errore nell\'importazione');
    } finally {
      setImpLoading(false);
    }
  };

  // ── Scarica .hbs o .json ───────────────────────────────────────────────
  const handleScarica = () => {
    const isStruttura = activeTab === 'struttura';
    const content = isStruttura ? contenutoHbs : datiJson;
    const filename = isStruttura
      ? `${nome.replace(/\s+/g, '-').toLowerCase()}.hbs`
      : `${nome.replace(/\s+/g, '-').toLowerCase()}-dati-campione.json`;
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  // ── Salva (solo design mode) ───────────────────────────────────────────
  const salva = async () => {
    if (!nome.trim()) { setSaveError('Il nome \u00e8 obbligatorio'); return; }
    setSaving(true); setSaveError(''); setSaveOk(false);
    try {
      // @api PUT /api/report-templates/:id {"nome":"string","contenutoHbs":"string","datiCampione":"string",...} -> {"success":true}
      await apiFetch(`/api/report-templates/${resolvedId}`, {
        method: 'PUT',
        body: JSON.stringify({
          nome: nome.trim(), descrizione: descrizione.trim() || null,
          moduloGestionale, contenutoHbs, datiCampione: datiJson,
          cssOverride: cssOverride.trim() || null,
          smtpId: smtpId ? parseInt(smtpId) : null,
          mittenteNomeOverride: mittenteNomeOverride.trim() || null,
          mittenteEmailOverride: mittenteEmailOverride.trim() || null,
          oggettoEmailDefault: oggettoEmailDefault.trim() || null,
          corpoEmailDefault: corpoEmailDefault.trim() || null,
        }),
      });
      setSaveOk(true);
      setTimeout(() => setSaveOk(false), 2000);
    } catch (e: unknown) { setSaveError(e instanceof Error ? e.message : String(e)); }
    finally { setSaving(false); }
  };

  // ── Invia email ────────────────────────────────────────────────────────
  const inviaEmail = async () => {
    if (!previewHtml) {
      alert('Genera prima l\'anteprima modificando il template o i dati');
      return;
    }

    const dest = [
      ...inviaSelezionati,
      ...inviaDestinatari.split(',').map(s => s.trim()).filter(Boolean)
    ];
    if (!dest.length) {
      alert('Inserisci almeno un destinatario');
      return;
    }

    const mc = metaDati?.mailConfig || null;

    // smtpId: sede > template
    const smtpIdToUse = (isRuntime && mc?.smtpId)
      ? mc.smtpId
      : template?.smtpId;

    if (!smtpIdToUse) {
      alert('Nessun SMTP configurato nel template o nella sede. Configuralo nella sidebar o nelle impostazioni sede.');
      return;
    }

    // Mittente: sede > template override
    const mittenteEmailToUse = (isRuntime && mc?.mittenteEmail)
      ? mc.mittenteEmail
      : (mittenteEmailOverride.trim() || null);
    const mittenteNomeToUse = (isRuntime && mc?.mittenteNome)
      ? mc.mittenteNome
      : (mittenteNomeOverride.trim() || null);

    // CCN dalla sede
    const ccnFromSede: string[] = [];
    if (isRuntime && mc?.ccnSelf && mittenteEmailToUse) ccnFromSede.push(mittenteEmailToUse);
    if (isRuntime && mc?.ccnAggiuntivi?.length) ccnFromSede.push(...mc.ccnAggiuntivi);

    setInviando(true);
    try {
      if (isRuntime && entitaId) {
        // RUNTIME → endpoint contestuale che fa provider + merge PDF allegati
        // @api POST /api/print/email/:templateId {"entitaType":"string","entitaId":"number","destinatari":["string"],"vista":"string?","conTotali":"boolean?","conAllegati":"boolean?"} -> {"success":true}
        await apiFetch(`/api/print/email/${resolvedId}`, {
          method: 'POST',
          body: JSON.stringify({
            entitaType,
            entitaId: parseInt(String(entitaId)),
            destinatari: dest,
            vista: vista || undefined,
            conTotali,
            conAllegati,
            ...extraOpts,
          }),
        });
      } else {
        // DESIGN → endpoint stateless con HTML già compilato
        // @api POST /api/print/email-from-html {"html":"string","destinatari":["string"],"oggetto":"string","smtpId":"number","ccn":["string"]} -> {"success":true}
        await apiFetch('/api/print/email-from-html', {
          method: 'POST',
          body: JSON.stringify({
            html:           previewHtml,
            destinatari:    dest,
            oggetto:        inviaOggetto || template?.nome || 'Documento',
            corpo:          inviaCorpo   || '',
            smtpId:         smtpIdToUse,
            mittenteNome:   mittenteNomeToUse,
            mittenteEmail:  mittenteEmailToUse,
            nomeAllegato:   entitaId
              ? `${entitaType}-${entitaId}${vista ? `-${vista.toLowerCase()}` : ''}.pdf`
              : 'documento.pdf',
            ccn:            ccnFromSede,
          }),
        });
      }
      setInviaOpen(false);
      alert('Email inviata con successo');
      // Notifica eventuali finestre aperte (es. PreventivoDettaglioPage) che
      // l'email è stata effettivamente spedita. Permette al chiamante di
      // proporre il "marca come inviato" senza polling.
      if (isRuntime && entitaId && typeof BroadcastChannel !== 'undefined') {
        try {
          const ch = new BroadcastChannel('bop-email-inviata');
          ch.postMessage({
            entitaType,
            entitaId: parseInt(String(entitaId)),
            destinatari: dest,
            vista: vista || null,
            inviatoIl: new Date().toISOString(),
          });
          ch.close();
        } catch (_) { /* best-effort */ }
      }
    } catch (e: unknown) { alert('Errore invio: ' + (e instanceof Error ? e.message : String(e))); }
    finally { setInviando(false); }
  };

  // ── PDF ─────────────────────────────────────────────────────────────────
  const scaricaPdf = async () => {
    if (!previewHtml) {
      alert('Genera prima l\'anteprima modificando il template o i dati');
      return;
    }
    // RUNTIME (con entitaId): usa endpoint contestuale che ricostruisce il JSON
    // dal data provider e applica il merge degli allegati PDF se richiesto.
    // DESIGN: usa pdf-from-html stateless per anteprima rapida sul template.
    if (isRuntime && entitaId) {
      const vistaSuffix = vista ? `-${vista.toLowerCase()}` : '';
      // @api POST /api/print/pdf/:templateId {"entitaType":"string","entitaId":"number","vista":"string?","conTotali":"boolean?","conAllegati":"boolean?"} -> stream PDF
      await apiDownload(
        `/api/print/pdf/${resolvedId}`,
        `${entitaType}-${entitaId}${vistaSuffix}.pdf`,
        {
          method: 'POST',
          body: JSON.stringify({
            entitaType,
            entitaId: parseInt(String(entitaId)),
            vista: vista || undefined,
            conTotali,
            conAllegati,
            ...extraOpts,
          }),
        }
      );
      return;
    }
    // Design mode — fallback stateless
    // @api POST /api/print/pdf-from-html {"html":"string"} -> stream PDF
    await apiDownload(
      '/api/print/pdf-from-html',
      `${(template?.nome || 'documento').replace(/\s+/g, '-').toLowerCase()}.pdf`,
      { method: 'POST', body: JSON.stringify({ html: previewHtml }) }
    );
  };

  // ── PDF completo (preventivo + allegati in coda) — solo runtime ────────
  const apriPdfCompleto = async () => {
    if (!isRuntime || !entitaId) return;
    // @api POST /api/print/pdf/:templateId {"entitaType":"string","entitaId":"number","vista":"string?","conTotali":"boolean?","conAllegati":"boolean?"} -> stream PDF
    await apiOpenBlob(
      `/api/print/pdf/${resolvedId}`,
      {
        method: 'POST',
        body: JSON.stringify({
          entitaType,
          entitaId: parseInt(String(entitaId)),
          vista: vista || undefined,
          conTotali,
          conAllegati,
          ...extraOpts,
        }),
      }
    );
  };

  // ── Stampa ──────────────────────────────────────────────────────────────
  const stampa = () => {
    if (!previewHtml) return;
    const w = window.open('', '_blank');
    if (!w) return;
    w.document.write(previewHtml + '<script>window.onload=function(){window.print()}<\/script>');
    w.document.close();
  };

  // ── Apri in nuova tab ──────────────────────────────────────────────────
  const apriInNuovaTab = () => {
    if (!previewHtml) return;
    const w = window.open('', '_blank');
    if (!w) return;
    w.document.write(previewHtml);
    w.document.close();
  };

  // ── Toggle pannelli ────────────────────────────────────────────────────
  const toggleSidebar = () => setSidebarOpen(prev => {
    const next = !prev;
    localStorage.setItem(SIDEBAR_KEY, String(next));
    return next;
  });
  const toggleLeft = () => setLeftOpen(prev => {
    const next = !prev;
    localStorage.setItem(LEFT_KEY, String(next));
    return next;
  });

  // ── AI editor assist ───────────────────────────────────────────────────
  const eseguiAI = async () => {
    if (!aiPrompt.trim() || !template || aiLoading) return;
    setAiLoading(true);
    setAiError('');
    setAiOk(false);
    try {
      // @api POST /api/impostazioni/ai/editor-assist {"societaId":"number","tipo":"struttura|dati","prompt":"string","contenutoAttuale":"string"} -> {"success":true,"data":{"contenuto":"string"}}
      const res = await apiFetch<{ success: boolean; data: { contenuto: string } }>(
        '/api/impostazioni/ai/editor-assist',
        {
          method: 'POST',
          body: JSON.stringify({
            societaId: template.societaId,
            tipo: activeTab,
            prompt: aiPrompt.trim(),
            contenutoAttuale: activeTab === 'struttura' ? contenutoHbs : datiJson,
          }),
        }
      );
      if (activeTab === 'struttura') {
        setContenutoHbs(res.data.contenuto);
      } else {
        setDatiJson(res.data.contenuto);
      }
      setAiOk(true);
      setAiPrompt('');
      setTimeout(() => setAiOk(false), 2500);
    } catch (e: unknown) {
      setAiError(e instanceof Error ? e.message : 'Errore AI');
    } finally {
      setAiLoading(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────
  if (loading) return <div className="bop-template-editor__loading">Caricamento...</div>;

  return (
    <div className="bop-template-editor">

      {/* ── TOPBAR ── */}
      <div className="bop-template-editor__topbar">
        {isRuntime ? (
          <span className="bop-template-editor__breadcrumb">
            {entitaType} #{entitaId}
            <ChevronIcon />
          </span>
        ) : (
          <span className="bop-template-editor__breadcrumb">
            Report <ChevronIcon />
          </span>
        )}
        <span className="bop-template-editor__name">{nome}</span>
        <span className="bop-badge bop-badge-info">{moduloGestionale}</span>
        <span className={`bop-template-editor__mode-pill bop-template-editor__mode-pill--${isRuntime ? 'runtime' : 'design'}`}>
          {isRuntime ? 'runtime' : 'design'}
        </span>
        <div className="bop-template-editor__spacer" />

        {!isRuntime && (
          <>
            <button className="bop-btn-secondary bop-btn-sm" onClick={() => window.close()}>Chiudi</button>
            <button className="bop-btn-primary bop-btn-sm" onClick={salva} disabled={saving}>
              {saving ? 'Salvo...' : saveOk ? '\u2713 Salvato' : 'Salva'}
            </button>
          </>
        )}
        {isRuntime && (
          <button className="bop-btn-secondary bop-btn-sm" onClick={() => window.close()}>Chiudi</button>
        )}

        <div className="bop-template-editor__topbar-divider" />
        {isRuntime && entitaId && numeroAllegatiPdfStampabili > 0 && conAllegati && (
          <button
            className="bop-btn-secondary bop-btn-sm"
            onClick={apriPdfCompleto}
            title={`Apre in nuova tab il PDF con ${numeroAllegatiPdfStampabili} ${numeroAllegatiPdfStampabili === 1 ? 'allegato' : 'allegati'} in coda`}
          >
            &#128065; PDF completo
          </button>
        )}
        <button className="bop-btn-secondary bop-btn-sm" onClick={scaricaPdf}>&darr; PDF</button>
        <button className="bop-btn-secondary bop-btn-sm" onClick={stampa}>&loz; Stampa</button>
        <button className="bop-btn-primary bop-btn-sm" onClick={() => setInviaOpen(o => !o)}>&equiv; Invia</button>
        <div className="bop-template-editor__topbar-divider" />
        <button
          className={`bop-btn-icon${leftOpen ? ' bop-btn-icon--active' : ''}`}
          onClick={toggleLeft} title="Pannello editor"
        >
          <PanelLeftIcon />
        </button>
        <button
          className={`bop-btn-icon${sidebarOpen ? ' bop-btn-icon--active' : ''}`}
          onClick={toggleSidebar} title="Impostazioni template"
        >
          <MenuIcon />
        </button>
      </div>

      {/* ── PANNELLO INVIA ── */}
      {inviaOpen && (
        <div className="bop-template-editor__invia-panel">
          {/* Info mittente dalla sede — visibile solo in runtime mode con mailConfig */}
          {isRuntime && metaDati?.mailConfig && (
            <div className={`bop-print-mittente-info ${metaDati.mailConfig.sedeConfigured ? 'bop-print-mittente-info--ok' : 'bop-print-mittente-info--warn'}`}>
              {metaDati.mailConfig.sedeConfigured ? (
                <>
                  <span className="bop-print-mittente-info__icon">✉</span>
                  <span>
                    Mittente sede:{' '}
                    <strong>
                      {metaDati.mailConfig.mittenteNome
                        ? `${metaDati.mailConfig.mittenteNome} <${metaDati.mailConfig.mittenteEmail}>`
                        : (metaDati.mailConfig.mittenteEmail || '—')}
                    </strong>
                    {metaDati.mailConfig.ccnSelf && (
                      <span className="bop-text-muted"> · CCN self attivo</span>
                    )}
                    {metaDati.mailConfig.ccnAggiuntivi?.length > 0 && (
                      <span className="bop-text-muted"> · CCN: {metaDati.mailConfig.ccnAggiuntivi.join(', ')}</span>
                    )}
                  </span>
                </>
              ) : (
                <>
                  <span className="bop-print-mittente-info__icon">⚠</span>
                  <span>Sede senza SMTP configurato — uso SMTP del template</span>
                </>
              )}
            </div>
          )}
          {destinatariSuggeriti.length > 0 ? (
            <div className="bop-template-editor__invia-section">
              <label className="bop-label">Destinatari suggeriti</label>
              {destinatariSuggeriti.map((d, i) => (
                <label key={i} className="bop-template-editor__dest-row">
                  <input type="checkbox"
                    checked={inviaSelezionati.includes(d.email)}
                    onChange={e => setInviaSelezionati(prev =>
                      e.target.checked ? [...prev, d.email] : prev.filter(x => x !== d.email)
                    )} />
                  <span className="bop-template-editor__dest-label">{d.label}</span>
                  <span className="bop-text-primary">{d.nome}</span>
                  <span className="bop-text-muted">{d.email}</span>
                </label>
              ))}
            </div>
          ) : (
            <div className="bop-template-editor__invia-empty">
              Nessun destinatario suggerito dal contesto
            </div>
          )}
          <div className="bop-form-group">
            <label className="bop-label">Altri destinatari</label>
            <input className="bop-input" value={inviaDestinatari}
              onChange={e => setInviaDestinatari(e.target.value)}
              placeholder="email1@ex.com, email2@ex.com" />
          </div>
          {isRuntime && entitaId && (
            <div className="bop-text-muted bop-template-editor__invia-runtime-hint">
              ℹ Oggetto e corpo verranno generati dal template salvato. Per personalizzarli per questa singola email, modifica il template e ri-salvalo prima di inviare.
            </div>
          )}
          <div className="bop-form-group">
            <label className="bop-label">Oggetto</label>
            <input className="bop-input" value={inviaOggetto} onChange={e => setInviaOggetto(e.target.value)} />
          </div>
          <div className="bop-form-group">
            <label className="bop-label">Messaggio</label>
            <textarea className="bop-input" rows={2} value={inviaCorpo} onChange={e => setInviaCorpo(e.target.value)} />
          </div>
          <div className="bop-flex bop-flex-between">
            <button className="bop-btn-secondary bop-btn-sm" onClick={() => setInviaOpen(false)}>Annulla</button>
            <button className="bop-btn-primary bop-btn-sm" onClick={inviaEmail} disabled={inviando}>
              {inviando ? 'Invio...' : 'Invia email'}
            </button>
          </div>
        </div>
      )}

      {/* ── BODY ── */}
      <div className="bop-template-editor__body">

        {/* Pannello sinistro — editor */}
        <div className={`bop-template-editor__left${leftOpen ? '' : ' bop-template-editor__left--collapsed'}`}>
          <div className="bop-template-editor__tab-bar">
            <button className={`bop-template-editor__tab${activeTab === 'struttura' ? ' bop-template-editor__tab--active' : ''}`}
              onClick={() => setActiveTab('struttura')}>
              <CodeIcon /> Struttura
            </button>
            <button className={`bop-template-editor__tab${activeTab === 'dati' ? ' bop-template-editor__tab--active' : ''}`}
              onClick={() => setActiveTab('dati')}>
              <DbIcon /> Dati
            </button>
            <div className="bop-template-editor__tab-actions">
              {activeTab === 'struttura' && !isRuntime && (
                <button className="bop-btn-secondary bop-btn-sm" onClick={() => hbsImportRef.current?.click()}>
                  Importa file
                </button>
              )}
              {activeTab === 'dati' && !isRuntime && (
                <button className="bop-btn-secondary bop-btn-sm" onClick={() => jsonImportRef.current?.click()}>
                  Importa file
                </button>
              )}
              {!isRuntime && (
                <button
                  className="bop-btn-secondary bop-btn-sm"
                  onClick={apriImportSocieta}
                  title="Carica struttura, dati campione o CSS da un template di un'altra società"
                >
                  Importa da società
                </button>
              )}
              <button className="bop-btn-secondary bop-btn-sm" onClick={handleScarica}>
                {activeTab === 'struttura' ? 'Scarica .hbs' : 'Scarica .json'}
              </button>
              {!isRuntime && (
                <button
                  className={`bop-btn-sm${aiOpen ? ' bop-btn-primary' : ' bop-btn-secondary'}`}
                  onClick={() => { setAiOpen(o => !o); setAiError(''); }}
                  title="Assistente AI (modifica con linguaggio naturale)"
                >
                  ✨ AI
                </button>
              )}
            </div>
          </div>

          <div className="bop-template-editor__editor-wrap">
            {/* Pannello AI — visibile solo in design mode quando aperto */}
            {aiOpen && !isRuntime && (
              <div className="bop-template-editor__ai-bar">
                <div className="bop-template-editor__ai-bar-row">
                  <span className="bop-template-editor__ai-icon">✨</span>
                  <textarea
                    className="bop-template-editor__ai-textarea"
                    value={aiPrompt}
                    onChange={e => setAiPrompt(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                        e.preventDefault();
                        eseguiAI();
                      }
                    }}
                    placeholder={
                      activeTab === 'struttura'
                        ? 'es. Aggiungi una riga di totale in fondo alla tabella SAL…'
                        : 'es. Aggiungi un campo "sconto" con valore 5% in ogni riga ordine…'
                    }
                    rows={2}
                    disabled={aiLoading}
                    autoFocus
                  />
                  <button
                    className={`bop-btn-sm bop-template-editor__ai-send${
                      aiOk ? ' bop-template-editor__ai-send--ok' : ' bop-btn-primary'
                    }`}
                    onClick={eseguiAI}
                    disabled={aiLoading || !aiPrompt.trim()}
                    title="Genera (Ctrl+Invio)"
                  >
                    {aiLoading ? 'Attendi…' : aiOk ? '✓ Applicato' : 'Genera'}
                  </button>
                </div>
                {aiError && (
                  <div className="bop-template-editor__ai-error">{aiError}</div>
                )}
                <div className="bop-template-editor__ai-hint">
                  Ctrl+Invio per inviare · Modifica {activeTab === 'struttura' ? 'template .hbs' : 'dati campione JSON'} · Salva manualmente dopo
                </div>
              </div>
            )}
            <Editor
              height="100%"
              language={activeTab === 'struttura' ? 'html' : 'json'}
              theme="vs-dark"
              value={activeTab === 'struttura' ? contenutoHbs : datiJson}
              onChange={val => activeTab === 'struttura'
                ? setContenutoHbs(val || '')
                : setDatiJson(val || '')}
              onMount={editor => {
                editor.onDidChangeCursorPosition(e =>
                  setCursorInfo(`Ln ${e.position.lineNumber}, Col ${e.position.column}`)
                );
              }}
              options={{
                readOnly: isRuntime && !can('impostazioni.report', 'edit'),
                minimap: { enabled: false },
                fontSize: 12, lineHeight: 20,
                wordWrap: 'on', scrollBeyondLastLine: false,
                automaticLayout: true,
              }}
            />
          </div>

          <div className={`bop-template-editor__editor-bar${isRuntime ? ' bop-template-editor__editor-bar--readonly' : ''}`}>
            <span className="bop-template-editor__editor-bar-item">{cursorInfo}</span>
            <span className="bop-template-editor__editor-bar-item">{activeTab === 'struttura' ? 'HTML \u00b7 Handlebars' : 'JSON'}</span>
            {isRuntime && !can('impostazioni.report', 'edit') && <span className="bop-template-editor__editor-bar-readonly">sola lettura</span>}
          </div>
        </div>

        {/* Pannello destro — anteprima */}
        <div className="bop-template-editor__right">
          <div className="bop-template-editor__preview-hdr">
            <span className="bop-template-editor__panel-title">Anteprima</span>
            <span className="bop-template-editor__preview-note">
              {isRuntime
                ? `Dati reali \u00b7 ${entitaType} #${entitaId}`
                : 'Dati campione \u00b7 aggiornamento automatico'}
            </span>
          </div>

          {previewError && (
            <div className="bop-template-editor__preview-error">{previewError}</div>
          )}

          <div className="bop-template-editor__preview-area">
            {isRuntime && entitaId && numeroAllegatiPdfStampabili > 0 && conAllegati && (
              <div className="bop-template-editor__preview-allegati-info">
                <span>&#8505;</span>
                <span>
                  L&apos;anteprima mostra solo il documento principale.{' '}
                  <strong>{numeroAllegatiPdfStampabili}</strong>{' '}
                  {numeroAllegatiPdfStampabili === 1 ? 'allegato verrà accodato' : 'allegati verranno accodati'}
                  {' '}al PDF finale.{' '}
                  <button
                    type="button"
                    className="bop-template-editor__preview-allegati-link"
                    onClick={apriPdfCompleto}
                  >
                    Vedi PDF completo &rarr;
                  </button>
                </span>
              </div>
            )}
            {previewHtml
              ? <iframe srcDoc={previewHtml} className="bop-template-editor__iframe" title="Anteprima" />
              : <div className="bop-empty-state">Modifica il template per vedere l&apos;anteprima</div>
            }
          </div>

          <div className="bop-template-editor__preview-foot">
            {previewTime && <span className="bop-text-muted">Aggiornata: {previewTime}</span>}
            <div className="bop-template-editor__preview-foot-spacer" />
            <button className="bop-btn-secondary bop-btn-sm" onClick={apriInNuovaTab}>Nuova tab</button>
          </div>
        </div>

        {/* Sidebar destra */}
        <div className={`bop-template-editor__sidebar${sidebarOpen ? '' : ' bop-template-editor__sidebar--collapsed'}`}>
          <div className="bop-template-editor__sb-section">
            <div className="bop-template-editor__sb-label">Identificazione</div>
            <div className="bop-form-group">
              <label className="bop-label">Nome</label>
              <input className="bop-input" value={nome} onChange={e => setNome(e.target.value)} readOnly={isRuntime} />
            </div>
            <div className="bop-form-group">
              <label className="bop-label">Modulo</label>
              <select className="bop-select" value={moduloGestionale} onChange={e => setModuloGestionale(e.target.value)} disabled={isRuntime}>
                {MODULI_REPORT.map(m => <option key={m} value={m}>{MODULO_REPORT_LABEL[m] || m}</option>)}
              </select>
            </div>
            <div className="bop-form-group">
              <label className="bop-label">Descrizione</label>
              <input className="bop-input" value={descrizione} onChange={e => setDescrizione(e.target.value)} readOnly={isRuntime} />
            </div>
          </div>
          <div className="bop-template-editor__sb-section">
            <div className="bop-template-editor__sb-label">CSS Override</div>
            <textarea className="bop-input" rows={4} value={cssOverride}
              onChange={e => setCssOverride(e.target.value)}
              readOnly={isRuntime}
              placeholder="/* stili aggiuntivi */" />
          </div>
          <div className="bop-template-editor__sb-section">
            <div className="bop-template-editor__sb-label">Email</div>
            <div className="bop-smtp-override-box">
              <div className="bop-smtp-override-box__title">SMTP e mittente</div>
              <select className="bop-select" value={smtpId} onChange={e => setSmtpId(e.target.value)} disabled={isRuntime}>
                <option value="">&mdash; Nessuno &mdash;</option>
                {smtpOptions.map(s => <option key={s.id} value={s.id}>{s.fromName} &mdash; {s.tipo}</option>)}
              </select>
              <input className="bop-input" value={mittenteNomeOverride}
                onChange={e => setMittenteNomeOverride(e.target.value)} readOnly={isRuntime}
                placeholder="Nome mittente override" />
              <input className="bop-input" type="email" value={mittenteEmailOverride}
                onChange={e => setMittenteEmailOverride(e.target.value)} readOnly={isRuntime}
                placeholder="email@societa.it" />
            </div>
            <div className="bop-form-group">
              <label className="bop-label">Oggetto default</label>
              <input className="bop-input" value={oggettoEmailDefault}
                onChange={e => setOggettoEmailDefault(e.target.value)} readOnly={isRuntime} />
            </div>
            <div className="bop-form-group">
              <label className="bop-label">Corpo default</label>
              <textarea className="bop-input" rows={3} value={corpoEmailDefault}
                onChange={e => setCorpoEmailDefault(e.target.value)} readOnly={isRuntime} />
            </div>
          </div>
          {!isRuntime && (
            <div className="bop-template-editor__sb-footer">
              {saveError && <div className="bop-text-danger bop-template-editor__sb-error">{saveError}</div>}
              <button className="bop-btn-primary bop-template-editor__sb-save-btn" onClick={salva} disabled={saving}>
                {saving ? 'Salvataggio...' : saveOk ? '\u2713 Salvato' : 'Salva template'}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ── Modale importa da altra società ── */}
      {impOpen && (
        <div className="bop-modal-overlay">
          <div className="bop-modal bop-modal--md">
            <div className="bop-modal__header">
              <h2 className="bop-modal__title">Importa da un&apos;altra società</h2>
              <button className="bop-btn-icon" onClick={() => setImpOpen(false)}>✕</button>
            </div>
            <div className="bop-modal__body">
              {impErrore && <div className="bop-alert bop-alert--danger">{impErrore}</div>}

              <div className="bop-import-report__intro">
                Sostituisce il contenuto dell&apos;editor con quello del template scelto.
                Le modifiche restano locali finché non premi <strong>Salva</strong>.
              </div>

              <div className="bop-form-group">
                <label className="bop-label">Società di origine</label>
                <select className="bop-select" value={impSorgenteId} onChange={e => cambiaImpSorgente(e.target.value)}>
                  <option value="">&mdash; Seleziona &mdash;</option>
                  {impSorgenti.map(s => (
                    <option key={s.id} value={String(s.id)}>{s.nome} ({s.numTemplate} template)</option>
                  ))}
                </select>
              </div>

              <div className="bop-form-group">
                <label className="bop-label">Template</label>
                <select
                  className="bop-select"
                  value={impTemplateId}
                  onChange={e => setImpTemplateId(e.target.value)}
                  disabled={!impSorgenteId || impTemplates.length === 0}
                >
                  <option value="">
                    {impSorgenteId && impTemplates.length === 0
                      ? '— Nessun template —'
                      : '— Seleziona —'}
                  </option>
                  {impTemplates.map(t => (
                    <option key={t.id} value={String(t.id)}>
                      {t.nome} — {t.moduloGestionale}{t.attivo ? '' : ' (inattivo)'}
                    </option>
                  ))}
                </select>
              </div>

              <div className="bop-section-label">Cosa importare</div>
              <div className="bop-import-report__scelte">
                <label className="bop-import-report__scelta">
                  <input type="checkbox" checked={impStruttura} onChange={e => setImpStruttura(e.target.checked)} />
                  <span>Struttura Handlebars (.hbs)</span>
                </label>
                <label className="bop-import-report__scelta">
                  <input type="checkbox" checked={impDati} onChange={e => setImpDati(e.target.checked)} />
                  <span>Dati campione (JSON)</span>
                </label>
                <label className="bop-import-report__scelta">
                  <input type="checkbox" checked={impCss} onChange={e => setImpCss(e.target.checked)} />
                  <span>CSS override</span>
                </label>
              </div>
            </div>
            <div className="bop-modal__footer">
              <button className="bop-btn-secondary" onClick={() => setImpOpen(false)}>Annulla</button>
              <button
                className="bop-btn-primary"
                onClick={eseguiImportSocieta}
                disabled={impLoading || !impTemplateId}
              >
                {impLoading ? 'Importazione...' : 'Importa nell’editor'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Input file nascosto per import .hbs */}
      <input ref={hbsImportRef} type="file" accept=".hbs"
        className="bop-template-editor__hidden-input" onChange={handleImportHbs} />

      {/* Input file nascosto per import .json */}
      <input ref={jsonImportRef} type="file" accept=".json"
        className="bop-template-editor__hidden-input" onChange={handleImportJson} />

      <ApiInspector docs={API_DOCS} />
    </div>
  );
}
